import { Ban, RotateCcw, ShoppingBag, Wallet } from "lucide-react";

import { formatMoney } from "../lib/format.js";
import { cn } from "../lib/cn.js";
import { type StoreTotalSales } from "../lib/reports.js";
import { Card } from "./ui/card.js";
import { InfoTooltip } from "./ui/info-tooltip.js";

/** The explanation behind the ⓘ, for a merchant holding this figure against Shopify. */
export const STORE_TOTAL_SALES_INFO =
  "Your store's sales for these dates, counted the way Shopify's Total sales report counts them: " +
  "the value of every order placed in these dates, minus orders cancelled and voided in these dates (even if placed earlier), " +
  "minus refunds made in these dates. Like Shopify, a cancelled order that was not voided (an unpaid order cancelled " +
  "without voiding its payment, or a paid order with no refund) stays a sale. Use this figure to compare with Shopify. " +
  "The table credits each order to a channel on the day it was placed, leaves out cancelled orders and " +
  "subtracts each order's refunds from that order, so its Total is a different figure and can change " +
  "when an order is cancelled or refunded later. " +
  "Refunds are included from when we started receiving them (September 2026).";

const count = (value: string | number): string => Number(value).toLocaleString("en-IN");
const plural = (value: string | number, one: string, many: string): string => (Number(value) === 1 ? one : many);

/**
 * THE STORE'S TOTAL SALES, AS SHOPIFY COUNTS THEM (2026-10-20), as four KPI cards above Source
 * Attribution: the figure itself, and the three amounts it is made of — each with how many orders or
 * refunds it covers, so "orders placed" never reads as a count of orders when it is money.
 */
export function StoreTotalSalesCards({ value }: { readonly value: StoreTotalSales }): React.JSX.Element {
  return (
    <section aria-label="Total sales (as in Shopify)" data-testid="store-total-sales" className="flex flex-col gap-2">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          highlight
          icon={<Wallet className="size-4" />}
          label="Total sales (as in Shopify)"
          info={STORE_TOTAL_SALES_INFO}
          value={formatMoney(value.total_sales)}
          sub="Compare this with Shopify's Total sales"
        />
        <Kpi
          icon={<ShoppingBag className="size-4" />}
          label="Sales from orders placed"
          value={formatMoney(value.orders_value)}
          sub={`${count(value.orders_placed)} ${plural(value.orders_placed, "order", "orders")} placed in these dates`}
        />
        <Kpi
          icon={<Ban className="size-4" />}
          label="Cancelled in these dates"
          value={`− ${formatMoney(value.cancellations)}`}
          sub={`${count(value.orders_cancelled)} voided ${plural(value.orders_cancelled, "order", "orders")}, including ones placed earlier`}
        />
        <Kpi
          icon={<RotateCcw className="size-4" />}
          label="Refunded in these dates"
          value={`− ${formatMoney(value.refunds)}`}
          sub={`${count(value.refund_count)} ${plural(value.refund_count, "refund", "refunds")} made in these dates`}
        />
      </div>
      <p className="text-muted-foreground m-0 text-xs">
        Cancellations and refunds count on the day they happened, as in Shopify. The table below credits each
        order, net of its refunds, on the day it was placed.
        {Number(value.orders_cancelled_kept) > 0 &&
          ` ${count(value.orders_cancelled_kept)} other cancelled ${plural(value.orders_cancelled_kept, "order", "orders")} ` +
            `(${formatMoney(value.cancellations_kept)}) ${Number(value.orders_cancelled_kept) === 1 ? "is" : "are"} not subtracted: ` +
            "not voided, so Shopify keeps them as sales."}
      </p>
    </section>
  );
}

function Kpi({
  icon,
  label,
  value,
  sub,
  info,
  highlight = false,
}: {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly value: string;
  readonly sub: string;
  readonly info?: string;
  readonly highlight?: boolean;
}): React.JSX.Element {
  return (
    <Card className={cn("gap-2 p-4", highlight && "border-primary/40 bg-primary/5")}>
      <div className="text-muted-foreground flex items-center justify-between text-xs font-medium tracking-wide uppercase">
        <span className="inline-flex items-center gap-1">
          {label}
          {info !== undefined && <InfoTooltip text={info} />}
        </span>
        <span className="text-primary">{icon}</span>
      </div>
      <div className={cn("text-foreground font-bold tracking-tight tabular-nums", highlight ? "text-3xl" : "text-2xl")}>{value}</div>
      <div className="text-muted-foreground text-[11px]">{sub}</div>
    </Card>
  );
}
